import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { CaseDiscountType, Coupon, CouponStatus, Deduction } from 'API';
import { BundleCouponInput } from 'components/common/BundleCouponInput/BundleCouponInput';
import Button from 'components/common/Button/Button';
import { CouponInput } from 'components/common/CouponInput/CouponInput';
import { DialogBox } from 'components/common/Dialog/DialogBox';
import Loader from 'components/common/Loader/Loader';
import { DisplayDiscountItems } from 'components/SpecialDiscount/DisplayDiscountItems/DisplayDiscountItems';
import AddSpecialDiscountInput from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscount/AddSpecialDiscount';
import { AddSpecialDiscountModal } from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscountModal/AddSpecialDiscountModal';
import { DiscountModalType } from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscountModal/types';
import { usePrevious } from 'hooks/use-previous';
import { useAuth } from 'providers/AuthProvider';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { OverlayLoaderContext } from 'providers/OverlayLoaderProvider';
import { ToastContext } from 'providers/ToastProvider';
import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createDiscount, removeDiscount, updateDiscount } from 'shared/api/discount.api';
import { ToastNotificationType } from 'shared/enums';
import { AnalyticsEventName } from 'shared/enums/analytics';
import {
  getApplyDiscountAnalytics,
  getNewDiscountInput,
  getUpdateDiscountDeductions,
  getUpdateDiscountPayload,
} from 'shared/helpers/discount.helper';
import { getAppliedSpecialDiscountItem, getDiscountCouponItem } from 'shared/helpers/invoice/invoice.helper';
import { convertOrderToLocalOrderInput } from 'shared/helpers/order-detail/order-detail.helper';
import { handleSubmitOrder } from 'shared/helpers/order-entry/order-entry.helper';
import { checkPriceOrTaxBeingCalculated } from 'shared/helpers/util.helper';
import { useLazyQueryFetcher } from 'shared/hooks/useLazyQueryFetcher';
import { LocalOrderInput } from 'shared/models';
import { ClassificationProduct } from 'shared/models/classification-product';
import { AnalyticsService } from 'shared/services/analytics.service';
import { useAddDiscountModalStore } from 'stores/useAddDiscountModalStore';
import { DiscountForRemove, DisplayDiscountItemType } from 'types/common';

/**
 * Props interface for the InvoicedDiscountSection component.
 * It includes a boolean flag indicating whether the component is in a loading state.
 */
interface InvoicedDiscountSectionProps {
  /** Indicates if the component is in a loading state */
  isLoading: boolean;

  /** List of product classifications to add material info inside order.orderItems */
  classifications: ClassificationProduct[];

  /** Flag to determine if discounts can be applied, where the order is being processed or is invoiced */
  isDiscountDisabled: boolean;

  /** Callback to refresh the order data */
  onRefresh?: (caseId?: string) => void;

  /** Callback to toggle skipping calculation complete toast for bundle cases */
  setSkipCompletedForBundle?: (state: boolean) => void;
}

/**
 * Component responsible for displaying invoiced discount details.
 * It renders a list of discount items, including coupons and other discounts, for invoiced cases.
 *
 * @param isLoading - Indicates whether the component is in a loading state or ready to render.
 * @param onRefresh - Callback function to refresh order data.
 * @param classifications - List of product classifications to enrich order items.
 * @param isDiscountDisabled - Boolean flag indicating if discounts are disabled due to processing or invoicing.
 * @param setSkipCompletedForBundle - Callback function to manage bundle processing state.
 *
 * @returns A JSX element representing the InvoicedDiscountSection component.
 */
export const InvoicedDiscountSection: FC<InvoicedDiscountSectionProps> = ({
  isLoading,
  onRefresh,
  classifications,
  isDiscountDisabled,
  setSkipCompletedForBundle,
}) => {
  const { invoiceCases, updateCase } = useInvoicingDetail();
  const toast = useContext(ToastContext);
  const { user } = useAuth();
  const isBundle = useMemo(() => invoiceCases.length > 1, [invoiceCases]);
  const setCurrentOrder = useAddDiscountModalStore(state => state.setCurrentOrder);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState('');
  const [isDeletingSpecialDiscount, setIsDeletingSpecialDiscount] = useState(false);
  const [discountForRemove, setDiscountForRemove] = useState<DiscountForRemove>();
  // List of pending coupon items used for UI rendering
  const [pendingCouponItems, setPendingCouponItems] = useState<DisplayDiscountItemType[]>([]);
  const [ordersWithCouponUpdate, setOrdersWithCouponUpdate] = useState<string[]>([]);
  const [disableCouponApply, setDisableCouponApply] = useState<boolean>(true);
  const { fetcher: removeDiscountFetcher } = useLazyQueryFetcher(removeDiscount);
  const { fetcher: updateDiscountFetcher } = useLazyQueryFetcher(updateDiscount);
  const currentOrderInStore = useAddDiscountModalStore(state => state.currentOrder);
  const [isDeletedSavedCoupon, setIsDeletedSavedCoupon] = useState(false);
  const [isApplyingCouponUpdate, setIsApplyingCouponUpdate] = useState(false);
  const overlayLoader = useContext(OverlayLoaderContext);

  const {
    discounts: selectedDiscounts,
    note,
    reason: selectedReason,
    selectedDiscountType,
  } = useAddDiscountModalStore(state => state.specialDiscount);
  const { fetcher: createDiscountFetcher } = useLazyQueryFetcher(createDiscount);

  /**
   * Contains all applied and currently pending coupon codes.
   * This is needed for checking for duplicate coupon codes.
   * Please see LMS1-8845.
   */
  const allAppliedAndPendingCouponCodes = useMemo(
    () => pendingCouponItems.map(coupon => coupon.discountCode),
    [pendingCouponItems]
  );

  const isOrderProcessing = useMemo(() => {
    return invoiceCases.some(order => checkPriceOrTaxBeingCalculated(order.metadata));
  }, [invoiceCases]);

  // Store previous state of `isOrderProcessing` to track changes
  const prevOrderProcessing = usePrevious(isOrderProcessing);

  /**
   * Opens the add special discount modal.
   */
  const onOpenModal = () => {
    const currentOrder = !isBundle ? invoiceCases[0] : null;
    setCurrentOrder(currentOrder);
    setShowModal(true);
  };
  const sortCouponsByStatus = (coupons: DisplayDiscountItemType[]) => {
    return coupons.sort((a, b) => {
      const statusA = a.status;
      const statusB = b.status;

      const isCouponAppliedA = statusA === CouponStatus.Applied;
      const isCouponNotAppliedA = statusA === CouponStatus.NotApplied;

      const isCouponAppliedB = statusB === CouponStatus.Applied;
      const isCouponNotAppliedB = statusB === CouponStatus.NotApplied;

      // Sort logic:
      // 1. Items with pending status at the top
      // 2. Applied coupons in the middle
      // 3. Not applied coupons at the bottom
      if (!statusA && statusB) return -1;
      if (statusA && !statusB) return 1;
      if (isCouponAppliedA && !isCouponAppliedB) return -1;
      if (isCouponNotAppliedA && !isCouponNotAppliedB) return 1;

      return 0;
    });
  };

  useEffect(() => {
    if (!isDiscountDisabled) {
      const couponItems = invoiceCases.flatMap(
        ({ appliedCoupons, orderNumber }) =>
          appliedCoupons?.map(coupon => getDiscountCouponItem(coupon, orderNumber)) || []
      );

      setPendingCouponItems(sortCouponsByStatus(couponItems));
    }
  }, [invoiceCases, isDiscountDisabled]);
  /**
   * Closes the add special discount modal.
   */
  const onCloseModal = () => {
    setCurrentOrder(null);
    setShowModal(false);
  };

  // List of applied special discount items
  const addedSpecialDiscountsItems = useMemo(() => {
    return invoiceCases.flatMap(({ appliedDiscounts, orderNumber }) =>
      appliedDiscounts ? getAppliedSpecialDiscountItem(appliedDiscounts, orderNumber) : []
    );
  }, [invoiceCases]);

  // List of applied special discount items
  const appliedDiscounts: Deduction[] = invoiceCases.flatMap(({ appliedDiscounts }) => {
    return appliedDiscounts || [];
  });

  // (TODO: Implement bundle support)
  /**
   * Adds a coupon to the invoice case.
   *
   * - Updates appliedCoupons to reflect changes in the UI.
   * - Updates coupons to modify the list of coupons submitted with the invoice.
   * - Calls updateCase to update the corresponding order within invoiceCases.
   *
   * @param coupon - Coupon.
   * @param caseId - orderNumber will be passed for bundled cases
   */
  const updateInvoiceOrderCoupons = (coupon: Coupon | null, caseId?: string) => {
    if (!coupon) return;

    const applyingCase = caseId
      ? invoiceCases.find(({ orderNumber }) => orderNumber === caseId) || invoiceCases[0]
      : invoiceCases[0];

    if (!applyingCase?.orderNumber) return;

    const { orderNumber, appliedCoupons = [] } = applyingCase;

    updateOrdersWithCoupon(orderNumber);
    // Updates `coupons` for invoice submission and `appliedCoupons` for UI rendering
    updateCase({
      orderNumber,
      appliedCoupons: [...(appliedCoupons || []), coupon],
    });
  };

  const updateOrdersWithCoupon = (orderNumber: string) => {
    setOrdersWithCouponUpdate(prevOrders => {
      // Check if the orderNumber already exists
      if (prevOrders.includes(orderNumber)) {
        return prevOrders; // Skip adding duplicate
      }
      return [...prevOrders, orderNumber]; // Add new orderNumber
    });
  };

  /**
   * Deletes a coupon from the case at the specified index.
   *
   * - Updates appliedCoupons to reflect changes in the UI.
   * - Updates coupons to modify the list of coupons submitted with the invoice.
   * - Calls updateCase to update the corresponding order within invoiceCases.
   *
   * @param index - The index of the coupon to be removed.
   */
  const deleteCoupon = (caseId: string, discountCode: string) => {
    const deletingCase = invoiceCases?.find(item => item.orderNumber === caseId);
    if (!deletingCase?.orderNumber) return;
    const { orderNumber, appliedCoupons = [] } = deletingCase || {};
    if (appliedCoupons?.find(item => item.code === discountCode)?.status) {
      setIsDeletedSavedCoupon(true);
    }
    updateOrdersWithCoupon(orderNumber);
    updateCase({
      orderNumber,
      appliedCoupons: (appliedCoupons || []).filter(item => item.code !== discountCode),
    });
  };

  /**
   * Applies a discount to the case.
   * Creates a new discount based on the user's input and updates the case details accordingly.
   */
  const onDiscountApply = async () => {
    if (currentOrderInStore) {
      const { orderNumber } = currentOrderInStore;
      const newDiscountInput = getNewDiscountInput(
        user?.displayName,
        selectedDiscountType,
        selectedDiscounts,
        note,
        orderNumber,
        selectedReason
      );

      try {
        await createDiscountFetcher(newDiscountInput);
        const deductionAnalytics = getApplyDiscountAnalytics(
          user?.displayName,
          selectedDiscountType,
          selectedDiscounts,
          note,
          orderNumber,
          selectedReason
        );
        AnalyticsService.track(AnalyticsEventName.CaseDeductionAdded, deductionAnalytics);
        // Passing the orderNumber to onRefresh retrieves the latest data for the applied discount order.
        // Based on order.metadata, further updates and banners will be handled using the OnRefresh function in invoicingPage.tsx.
        onRefresh?.(orderNumber);
        onCloseModal();
      } catch (err) {
        toast.notify(
          'Failed to add special discount. Please make sure you entered valid values.',
          ToastNotificationType.Error
        );
      }
    }
  };

  // Function to update order coupons for a given case ID
  const updateOrderCoupons = useCallback(
    async (caseId: string) => {
      // Find the corresponding order by case ID
      const order = invoiceCases.find(item => item.orderNumber === caseId);
      if (!order) return;

      const { appliedCoupons, orderNumber, utcConversionTimeZoneCode } = order;
      const currentUserName = user?.displayName || '';

      // Convert order data to local format
      const newData = convertOrderToLocalOrderInput({ orderData: order, currentUserName });
      if (!newData) return;

      try {
        const orderData: LocalOrderInput = {
          ...newData,
          originFacility: order.originFacility,
          utcConversionTimeZone: utcConversionTimeZoneCode,
          orderItems: newData.orderItems.map(item => {
            // Need to map material and restoration type, since these are initially empty string.
            const materialInfo = classifications.find(material => material.productCode === item.productCode);
            return {
              ...item,
              material: materialInfo?.materialName || '',
              restorationType: materialInfo?.restorationName || '',
            };
          }),
        };

        // update order with the updated coupons
        const updatedOrder = await handleSubmitOrder({
          order: {
            ...orderData,
            coupons: appliedCoupons || [],
          },
          orderUpdateId: caseId,
          currentUserName,
          overlayLoader,
          toast,
          hasAppliedAlerts: false,
        });

        // Trigger onRefresh after updating the order to recalculate the pricing and to show the price calculating and completed toast
        onRefresh?.(orderNumber);

        // Remove processed order from pending updates
        setOrdersWithCouponUpdate(prevOrders => prevOrders.filter(prevOrderNumber => prevOrderNumber !== caseId));

        return updatedOrder;
      } catch (error) {
        console.error(error);
      } finally {
        setIsApplyingCouponUpdate(false);
      }
    },
    [user?.displayName, invoiceCases, classifications, onRefresh, overlayLoader, toast] // Dependencies
  );

  /**
   * Effect to manage the state of coupon application.
   * - Enables the "Apply Coupon" button if there are pending coupons (without a status) or if a saved coupon was deleted.
   * - Disables the "Apply Coupon" button when no pending coupons exist and clears the `ordersWithCouponUpdate` state.
   * - Resets the `isApplyingCouponUpdate` flag if it was previously set.
   */
  useEffect(() => {
    const hasPendingCoupons = pendingCouponItems.find(item => !item.status);
    if (isDeletedSavedCoupon || hasPendingCoupons) {
      setDisableCouponApply(false);
    } else {
      setDisableCouponApply(true);
      setOrdersWithCouponUpdate([]);
      if (isApplyingCouponUpdate) {
        setIsApplyingCouponUpdate(false);
      }
    }
  }, [isDeletedSavedCoupon, pendingCouponItems, isApplyingCouponUpdate]);

  useEffect(() => {
    const processBundleNextOrder = async () => {
      // Proceed if processing has completed, there are pending orders, and coupon application is ongoing
      if (!isOrderProcessing && prevOrderProcessing && ordersWithCouponUpdate.length > 0 && !!applyingCoupon) {
        const caseId = ordersWithCouponUpdate[0]; // Get next order in queue

        // Prevent redundant processing of the same order
        if (applyingCoupon === caseId) return;

        setApplyingCoupon(caseId);

        // If processing the last order, reset skip completed price calculation toast flag
        if (ordersWithCouponUpdate.length === 1) {
          setSkipCompletedForBundle?.(true);
        }

        await updateOrderCoupons(caseId); // Process the order
      }
      // If all orders are processed, reset applyingCoupon state
      else if (ordersWithCouponUpdate.length === 0) {
        setApplyingCoupon('');
        setIsDeletedSavedCoupon(false);
        // Disable the apply coupon as there is no coupon updates to submit
        return;
      } else return;
    };

    processBundleNextOrder();
  }, [
    isOrderProcessing,
    ordersWithCouponUpdate,
    applyingCoupon,
    prevOrderProcessing,
    setSkipCompletedForBundle,
    updateOrderCoupons,
  ]);

  // Function to initiate coupon application process
  const onCouponApply = async () => {
    setIsApplyingCouponUpdate(true);
    // Get orders that require coupon updates
    const casesHavingUpdate = invoiceCases.filter(invoice => ordersWithCouponUpdate.includes(invoice.orderNumber));

    // Fetch the first order to be processed
    const orderNumber = casesHavingUpdate[0]?.orderNumber;
    setApplyingCoupon(orderNumber);

    // If there are multiple orders, mark bundle processing as active which skips the pricing completed toast till the last order
    if (casesHavingUpdate.length > 1) {
      setSkipCompletedForBundle?.(false);
    }

    try {
      await updateOrderCoupons(orderNumber);
    } catch (e) {
      console.error(e);
    } finally {
      setIsApplyingCouponUpdate(false);
    }
  };

  /**
   * Renders the coupon input section for invoices with a single case(TODO: Implement bundle support).
   * - If multiple cases exist, this component returns `null` (intended for single-case invoices only for now).
   *
   * @returns JSX.Element | null - The coupon input section if applicable, otherwise `null`.
   */
  const RenderCouponInput = () => {
    return (
      <div className="bg-white border p-4 sm:rounded-lg shadow-sm">
        <div className="font-medium text-sm text-gray-700 mb-2">Coupon</div>
        <Fragment>
          {!isBundle && !isDiscountDisabled && (
            <CouponInput
              isLoading={isLoading}
              billingAccountId={invoiceCases[0]?.billingAccountId || ''}
              providerId={invoiceCases[0]?.providerId}
              onUpdateCoupon={coupon => updateInvoiceOrderCoupons(coupon)}
              coupons={allAppliedAndPendingCouponCodes}
            />
          )}
          <DisplayDiscountItems
            items={pendingCouponItems}
            onDelete={
              isApplyingCouponUpdate
                ? undefined
                : (_, caseId, discountCode) => {
                    deleteCoupon(caseId, discountCode);
                  }
            }
            className=" my-2"
            showCaseId={isBundle}
            showLoading={true}
            hideStatus={true}
          />
          {isBundle && (
            <BundleCouponInput
              billingAccountId={invoiceCases[0]?.billingAccountId || ''}
              providerId={invoiceCases[0]?.providerId}
              onUpdateCoupon={(coupon, caseId) => updateInvoiceOrderCoupons(coupon, caseId)}
              coupons={pendingCouponItems || []}
              bundledCases={invoiceCases}
              disableAddCoupon={!!applyingCoupon.length}
            />
          )}
          <Button
            disabled={isApplyingCouponUpdate || disableCouponApply}
            onClick={() => onCouponApply()}
            variant={'bordered'}
            className="mt-5 text-sm text-gray-700"
          >
            {isApplyingCouponUpdate ? <Loader show={true} className="text-gray-500" spin /> : 'Apply Coupon'}
          </Button>
        </Fragment>
      </div>
    );
  };

  /**
   * Handles removing a special discount from the case.
   */
  const removeSpecialDiscount = async () => {
    const discount = appliedDiscounts.find(item => item.discountId === discountForRemove?.discountId);
    const discounts = [...appliedDiscounts];
    const orderNumber = discount?.orderNumber || '';
    if (!discount) return; // if we can't find it, just return

    const isEntireCase = discount.type === CaseDiscountType.EntireCase;
    setIsDeletingSpecialDiscount(true);

    try {
      /**
       * If there is only one deduction, then we delete the item by discountId and if there are more than one, then we update the discount
       * Because we don't have a way to delete a single deduction from the database
       *
       * That's why we need to check the length of the deductions array here
       *
       * If length is greater than 1, we can just update the discount with the remaining deductions
       */
      if (discount.deductions.length <= 1 || isEntireCase) {
        await removeDiscountFetcher({ discountId: discount.discountId });
        updateCase({
          orderNumber,
          appliedDiscounts: discounts.splice(0, 1),
        });
      } else {
        const updatePayload = getUpdateDiscountPayload(discount, discountForRemove, selectedDiscountType);
        await updateDiscountFetcher(updatePayload);
        const updatedDiscounts = discounts.map(item => {
          if (item.discountId === discountForRemove?.discountId) {
            item.deductions = getUpdateDiscountDeductions(discount, discountForRemove);
          }
          return item;
        });
        updateCase({
          orderNumber,
          appliedDiscounts: updatedDiscounts,
        });
      }

      onRefresh?.(orderNumber);
      AnalyticsService.track(AnalyticsEventName.CaseDeductionRemoved, {
        caseNumber: discount?.orderNumber || '',
      });
    } catch (err) {
      toast.notify('Failed to remove special discount.', ToastNotificationType.Error);
    } finally {
      setIsDeletingSpecialDiscount(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="font-medium flex">
        3. Add discount<div className="font-medium text-gray-500 ml-1">(optional)</div>
      </div>
      <div className="w-full">
        <RenderCouponInput />
      </div>
      <AddSpecialDiscountInput
        onAddButtonClick={onOpenModal}
        isLoading={isLoading}
        addedSpecialDiscountsItems={addedSpecialDiscountsItems}
        disabled={isDiscountDisabled}
        onDeleteItem={(discountId: string, description: string) => {
          setShowConfirmModal(true);
          setDiscountForRemove({ discountId, description });
        }}
        showCaseId={isBundle}
      />
      <AddSpecialDiscountModal
        showModal={showModal}
        onClose={onCloseModal}
        onApply={onDiscountApply}
        type={DiscountModalType.SpecialDiscount}
        bundledCases={invoiceCases}
      />
      {showConfirmModal && (
        <DialogBox
          title="Are you sure you want to remove this discount?"
          onCancel={() => setShowConfirmModal(false)}
          icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-600" />}
          confirmText="Remove"
          onConfirm={() => removeSpecialDiscount()}
          confirmButtonDisabled={isDeletingSpecialDiscount}
        >
          <div className="text-base text-gray-500"></div>
        </DialogBox>
      )}
    </div>
  );
};
